// packages/shared-types/src/llm/selectableModels.ts
// SSOT du catalogue de modèles sélectionnables par un super-admin (add-in Word).
// Slugs vérifiés vs apps/backend/lib/llm/catalog.ts + API OpenRouter.
// Source slugs OpenRouter: https://openrouter.ai/api/v1/models (verified 2026-06-19).
import { z } from 'zod';

export interface SelectableModel {
  readonly slug: string;       // slug canonique provider/modèle
  readonly label: string;      // libellé UI
  readonly zone: 'EU' | 'US' | 'Unknown'; // résidence d'hébergement (badge UI)
  readonly reasoning: boolean; // expose le sélecteur de reasoning ?
  readonly chat: boolean;      // sélectionnable pour le chat agentique
  /**
   * Crans d'effort que ce modèle accepte réellement, dans le vocabulaire du provider.
   *
   * MIROIR de `MODEL_CATALOG.reasoningEfforts` (apps/backend/lib/llm/catalog.ts), que ce
   * package ne peut pas importer (les apps dépendent de packages/, jamais l'inverse). La
   * dérive est verrouillée par un test backend qui compare les deux listes.
   *
   * POURQUOI l'exposer ici : sans elle, le sélecteur super-admin proposait un menu plat
   * `none|low|medium|high|xhigh` pour TOUS les modèles. Depuis que le backend REFUSE un
   * cran hors échelle (2026-07-27), ce menu offrait des crans qui lèvent — `xhigh` sur les
   * six routes DeepSeek/GLM-5.2 — et cachait `max`, le seul autre cran que GLM-5.2 sert.
   * `[]` = pas de cran gradué : le sélecteur ne montre alors que l'interrupteur `none`.
   */
  readonly reasoningEfforts: readonly ReasoningEffortLevel[];
}

export const SELECTABLE_MODELS = [
  { slug:'tensorix/deepseek/deepseek-v4.1-flash-1017', label:'DeepSeek V4.1 Flash 1017', zone:'EU', reasoning:true,  chat:true, reasoningEfforts:['high','max'] },
  { slug:'tensorix/deepseek/deepseek-v4-flash-0731',   label:'DeepSeek V4-Flash 0731', zone:'EU', reasoning:true,  chat:true, reasoningEfforts:['high','max'] },
  { slug:'tensorix/z-ai/glm-5.1',                 label:'GLM-5.1',                zone:'EU', reasoning:true,  chat:true, reasoningEfforts:[] },
  // No official Tensorix residency source or signed DPA reference is stored in repo/env on 2026-06-24.
  // Keep zone=Unknown instead of claiming EU residency for these provider-specific routes.
  { slug:'tensorix/z-ai/glm-5.3',                 label:'GLM-5.3 (Tensorix)',    zone:'Unknown', reasoning:true,  chat:true, reasoningEfforts:['high','max'] },
  { slug:'tensorix/minimax/minimax-m3',           label:'MiniMax M3 (Tensorix)', zone:'Unknown', reasoning:true,  chat:true, reasoningEfforts:[] },
  { slug:'openrouter/z-ai/glm-5.2',               label:'GLM-5.2 (OpenRouter)',  zone:'US', reasoning:true,  chat:true, reasoningEfforts:['high','max'] },
  { slug:'openrouter/minimax/minimax-m3',         label:'MiniMax M3 (OpenRouter)', zone:'US', reasoning:true,  chat:true, reasoningEfforts:[] },
  { slug:'openrouter/deepseek/deepseek-v4.1-flash-1017', label:'DeepSeek V4.1 Flash 1017 (OR)', zone:'US', reasoning:true,  chat:true, reasoningEfforts:['high','max'] },
  { slug:'openrouter/deepseek/deepseek-v4-flash', label:'DeepSeek V4-Flash (OR)', zone:'US', reasoning:true,  chat:true, reasoningEfforts:['high','max'] },
] as const satisfies readonly SelectableModel[];

/** Compile-time union of the actual UI catalogue; unlike Zod's mapped tuple it preserves literals. */
export type SelectableModelSlug = (typeof SELECTABLE_MODELS)[number]['slug'];

/** Runtime boundary for values supplied by an administrator over HTTP. */
export const SelectableModelSlugSchema: z.ZodType<SelectableModelSlug, z.ZodTypeDef, string> = z
  .string()
  .refine(
    (value): value is SelectableModelSlug => SELECTABLE_MODELS.some(model => model.slug === value),
    'Le modèle doit être un slug de SELECTABLE_MODELS',
  );

// Doit rester identique à l'union ReasoningConfig['effort'] de apps/backend/lib/llm/types.ts.
export const ReasoningEffortSchema = z.enum(['none','low','medium','high','xhigh','max']);
export type ReasoningEffortLevel = z.infer<typeof ReasoningEffortSchema>;
